/**
 * CardSetupFlow
 *
 * The one place Stripe card capture lives (PC-3). Two layouts:
 *
 * `singleStep` (publish / agent-support dialogs): ONE form with the billing address and the card
 * together and one submit. Deferred-intent Elements (mode 'setup'): elements.submit() ->
 * requestSetupIntent(address) -> stripe.confirmSetup({ elements, clientSecret }) -> onConfirmed.
 *
 * Default (CheckoutPage): two memoized Elements phases:
 *   Phase 1 (address): Stripe Address Element (no clientSecret) -> requestSetupIntent(address)
 *   Phase 2 (card):    Stripe Payment Element with the SetupIntent clientSecret ->
 *                      stripe.confirmSetup() -> onConfirmed(setupIntentId)
 *
 * Used by CheckoutPage (subscribe) and the publish / agent-support dialogs (save a card).
 * With no `testIdPrefix` the test ids are the original CheckoutPage ones.
 */

import { useState, useMemo, type ReactNode, type FormEvent } from 'react';
import {
  Elements,
  PaymentElement,
  AddressElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { getStripe } from '../../lib/stripe';
import { buildStripeAppearance } from '../../lib/stripe-appearance';
import { useTheme } from '../../contexts/theme.context';
import { Button } from '../ui/button';
import type { BillingAddress } from '../../api/billing';

/** Dialog surface shared by the payment dialogs: centered modal >=768px, bottom sheet below. */
export const PAYMENT_DIALOG_CLASS =
  'max-w-[520px] max-h-[92vh] overflow-y-auto max-md:top-auto max-md:bottom-0 max-md:translate-y-0 max-md:rounded-t-xl max-md:rounded-b-none max-md:data-[state=open]:slide-in-from-bottom max-md:data-[state=closed]:slide-out-to-bottom';

/** Destructive / warning alert used by the payment dialogs (declined, in progress, ...). */
export function PaymentAlert({
  tone,
  testId,
  children,
}: {
  tone: 'destructive' | 'warning';
  testId: string;
  children: ReactNode;
}) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className={
        tone === 'destructive'
          ? 'rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive'
          : 'rounded-lg border border-warning/25 bg-warning/10 p-3 text-sm text-warning'
      }
    >
      {children}
    </div>
  );
}

type Phase = 'address' | 'payment';

interface CardSetupFlowProps {
  /** Create a SetupIntent for the collected billing address. May throw; the message is shown inline. */
  requestSetupIntent: (billingAddress: BillingAddress) => Promise<{ clientSecret: string }>;
  /** The SetupIntent succeeded in the browser — notify the server. May throw; shown inline. */
  onConfirmed: (setupIntentId: string) => Promise<void>;
  onCancel: () => void;
  /** Content above the form of each phase (a node, or a function of the phase). */
  summary?: ReactNode | ((phase: Phase) => ReactNode);
  /** Prefix for data-testid values; omitted -> the original CheckoutPage ids. */
  testIdPrefix?: string;
  /** Label of the final (card) submit button. */
  submitLabel: string;
  /** Show address + card together with one submit (the payment dialogs). */
  singleStep?: boolean;
  /** Heading above the address element. */
  addressHeading?: string;
  /** E2E-only opt-in that disables Google Places autocomplete (see CheckoutPage). */
  disableAddressAutocomplete?: boolean;
  /** Copy under the card element. */
  cardNote?: ReactNode;
}

function ids(prefix: string | undefined) {
  if (!prefix) {
    // Literal ids so the checkout flow's data-testid closed set (RW-TH-7 baseline) stays greppable.
    return {
      addressForm: 'address-form',
      taxNotice: 'tax-notice',
      addressContinue: 'address-continue-button',
      addressCancel: 'address-cancel-button',
      paymentForm: 'payment-form',
      paymentError: 'payment-error',
      submit: 'subscribe-button',
      paymentCancel: 'payment-cancel-button',
    };
  }
  const p = `${prefix}-`;
  return {
    addressForm: `${p}address-form`,
    taxNotice: `${p}tax-notice`,
    addressContinue: `${p}address-continue-button`,
    addressCancel: `${p}address-cancel-button`,
    paymentForm: `${p}payment-form`,
    paymentError: `${p}payment-error`,
    submit: `${prefix}-save-card-button`,
    paymentCancel: `${p}payment-cancel-button`,
  };
}

function ErrorAlert({ message, testId }: { message: string; testId?: string }) {
  return (
    <div
      className="px-4 py-3 bg-destructive/5 border border-destructive/20 rounded-lg mb-4"
      role="alert"
      data-testid={testId}
    >
      <p className="m-0 text-sm text-destructive">{message}</p>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Phase 1 — must be inside an Elements provider (no clientSecret required)
// -----------------------------------------------------------------------------

function AddressStep({
  onAddressConfirmed,
  onCancel,
  summary,
  testIds,
  submitLabel,
  heading,
  disableAddressAutocomplete,
}: {
  onAddressConfirmed: (address: BillingAddress) => Promise<void>;
  onCancel: () => void;
  summary: ReactNode;
  testIds: ReturnType<typeof ids>;
  submitLabel: string;
  heading: string;
  disableAddressAutocomplete: boolean;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    const addressElement = elements.getElement('address');
    if (!addressElement) {
      setError('Address form not ready');
      setSubmitting(false);
      return;
    }

    const { complete, value } = await addressElement.getValue();
    if (!complete) {
      setError('Please complete your billing address');
      setSubmitting(false);
      return;
    }

    const addr = value.address;
    try {
      await onAddressConfirmed({
        line1: addr.line1,
        line2: addr.line2 ?? undefined,
        city: addr.city,
        state: addr.state,
        postalCode: addr.postal_code,
        country: addr.country,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize checkout');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid={testIds.addressForm}>
      <div className="mb-5">
        {summary}

        <h2 className="text-lg font-semibold text-foreground m-0 mb-3">{heading}</h2>
        <p className="text-sm text-muted-foreground mb-4" data-testid={testIds.taxNotice}>
          Taxes will be calculated based on your billing address.
        </p>

        <AddressElement
          options={{
            mode: 'billing',
            allowedCountries: ['US'],
            // Disable Google Places autocomplete so E2E tests can fill individual address
            // fields directly without driving the autocomplete UI. `!import.meta.env.PROD`
            // alone is NOT sufficient: staging is deployed from the same production-mode
            // `vite build` as real production (no separate "staging" Vite mode), so PROD is
            // true there too — the E2E-only affordance silently never applied on staging,
            // where the Playwright suite actually runs. `disableAddressAutocomplete` is an
            // explicit opt-in driven by a `?disableAddressAutocomplete=1` query param the
            // E2E test appends — never inferred from build mode or hostname — so real
            // staging traffic keeps the production autocomplete experience.
            ...(disableAddressAutocomplete && { autocomplete: { mode: 'disabled' } }),
          }}
        />
      </div>

      {error && <ErrorAlert message={error} />}

      <div className="flex gap-3 mt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
          className="flex-1 min-h-[44px]"
          data-testid={testIds.addressCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || submitting}
          className="flex-[2] min-h-[44px]"
          data-testid={testIds.addressContinue}
        >
          {submitting ? 'Calculating...' : submitLabel}
        </Button>
      </div>
    </form>
  );
}

// -----------------------------------------------------------------------------
// Shared by the card-bearing steps (phase 2 of the two-step flow, and the single step)
// -----------------------------------------------------------------------------

type StripeInstance = NonNullable<ReturnType<typeof useStripe>>;
type StripeElementsInstance = NonNullable<ReturnType<typeof useElements>>;

/**
 * stripe.confirmSetup(), NOT confirmPayment(). Resolves to the user-facing failure message, or
 * null once the SetupIntent succeeded and `onConfirmed` has run. `clientSecret` is only passed for
 * the deferred-intent (single step) flow; the two-step flow's Elements already carries it.
 */
async function confirmSetupAndNotify(
  stripe: StripeInstance,
  elements: StripeElementsInstance,
  clientSecret: string | undefined,
  onConfirmed: (setupIntentId: string) => Promise<void>,
): Promise<string | null> {
  const { error: stripeError, setupIntent } = await stripe.confirmSetup({
    elements,
    ...(clientSecret ? { clientSecret } : {}),
    redirect: 'if_required',
    confirmParams: {
      return_url: `${window.location.origin}/landlord/settings/billing/checkout/success`,
    },
  });

  if (stripeError) return stripeError.message || 'Payment confirmation failed';

  if (setupIntent && setupIntent.status === 'succeeded') {
    await onConfirmed(setupIntent.id);
    return null;
  }
  return 'Payment method could not be confirmed. Please try again.';
}

/**
 * Form shell of a card-bearing step: the form, inline error, and Cancel / submit buttons. `attempt`
 * resolves to a failure message (shown inline) or null; a throw is shown as its message.
 */
function CardFormShell({
  testIds,
  submitLabel,
  onCancel,
  attempt,
  children,
}: {
  testIds: ReturnType<typeof ids>;
  submitLabel: string;
  onCancel: () => void;
  attempt: (stripe: StripeInstance, elements: StripeElementsInstance) => Promise<string | null>;
  children: ReactNode;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      const failure = await attempt(stripe, elements);
      if (failure) {
        setError(failure);
        setProcessing(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid={testIds.paymentForm}>
      {children}

      {error && <ErrorAlert message={error} testId={testIds.paymentError} />}

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 min-h-[44px]"
          data-testid={testIds.paymentCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || !elements || processing}
          className="flex-[2] min-h-[44px]"
          data-testid={testIds.submit}
        >
          {processing ? 'Processing...' : submitLabel}
        </Button>
      </div>
    </form>
  );
}

// -----------------------------------------------------------------------------
// Phase 2 — requires the SetupIntent clientSecret (carried by the surrounding Elements)
// -----------------------------------------------------------------------------

function CardStep({
  onCancel,
  onConfirmed,
  summary,
  testIds,
  submitLabel,
  cardNote,
}: {
  onCancel: () => void;
  onConfirmed: (setupIntentId: string) => Promise<void>;
  summary: ReactNode;
  testIds: ReturnType<typeof ids>;
  submitLabel: string;
  cardNote: ReactNode;
}) {
  return (
    <CardFormShell
      testIds={testIds}
      submitLabel={submitLabel}
      onCancel={onCancel}
      attempt={(stripe, elements) => confirmSetupAndNotify(stripe, elements, undefined, onConfirmed)}
    >
      <div className="mb-5">
        {summary}
        {cardNote}
        <PaymentElement />
      </div>
    </CardFormShell>
  );
}

// -----------------------------------------------------------------------------
// Single step — address + card in one form (deferred SetupIntent)
// -----------------------------------------------------------------------------

function SingleStep({
  requestSetupIntent,
  onConfirmed,
  onCancel,
  summary,
  testIds,
  submitLabel,
  heading,
  cardNote,
  disableAddressAutocomplete,
}: {
  requestSetupIntent: CardSetupFlowProps['requestSetupIntent'];
  onConfirmed: (setupIntentId: string) => Promise<void>;
  onCancel: () => void;
  summary: ReactNode;
  testIds: ReturnType<typeof ids>;
  submitLabel: string;
  heading: string;
  cardNote: ReactNode;
  disableAddressAutocomplete: boolean;
}) {
  const attempt = async (stripe: StripeInstance, elements: StripeElementsInstance) => {
    const { error: submitError } = await elements.submit();
    if (submitError) return submitError.message || 'Please check your card details';

    const addressElement = elements.getElement('address');
    const { complete, value } = addressElement
      ? await addressElement.getValue()
      : { complete: false, value: null };
    if (!complete || !value) return 'Please complete your billing address';

    const addr = value.address;
    const { clientSecret } = await requestSetupIntent({
      line1: addr.line1,
      line2: addr.line2 ?? undefined,
      city: addr.city,
      state: addr.state,
      postalCode: addr.postal_code,
      country: addr.country,
    });

    return confirmSetupAndNotify(stripe, elements, clientSecret, onConfirmed);
  };

  return (
    <CardFormShell testIds={testIds} submitLabel={submitLabel} onCancel={onCancel} attempt={attempt}>
      <div className="mb-5 flex flex-col gap-4">
        {summary}
        <div>
          <h2 className="text-lg font-semibold text-foreground m-0 mb-1">{heading}</h2>
          <p className="text-sm text-muted-foreground mb-3" data-testid={testIds.taxNotice}>
            Taxes will be calculated based on your billing address.
          </p>
          <AddressElement
            options={{
              mode: 'billing',
              allowedCountries: ['US'],
              // E2E-only opt-in, same contract as the two-step flow above.
              ...(disableAddressAutocomplete && { autocomplete: { mode: 'disabled' } }),
            }}
          />
        </div>
        <div>
          {cardNote}
          <PaymentElement />
        </div>
      </div>
    </CardFormShell>
  );
}

// -----------------------------------------------------------------------------
// Flow
// -----------------------------------------------------------------------------

export function CardSetupFlow({
  requestSetupIntent,
  onConfirmed,
  onCancel,
  summary,
  testIdPrefix,
  submitLabel,
  singleStep = false,
  addressHeading = 'Billing Address',
  disableAddressAutocomplete = false,
  cardNote = null,
}: CardSetupFlowProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const stripePromise = getStripe();
  const testIds = useMemo(() => ids(testIdPrefix), [testIdPrefix]);

  // Memoize Elements options to prevent new object references on every render. The Elements
  // useEffect lists `options` as a dependency — an inline object literal would create a new
  // reference on every parent re-render (React Query polling, state updates, etc.),
  // restarting the Stripe promise chain and leaving `elements` perpetually null.
  // `paymentMethodTypes: ['card']` must mirror the server-created SetupIntent's payment_method_types
  // (payment-method.service createSetupIntent): Stripe rejects confirming an Elements instance that
  // uses automatic payment methods against an intent created with explicit types.
  // `appearance` follows the landlord light/dark theme (Stripe's iframes can't read our CSS).
  const { resolved: resolvedTheme } = useTheme();
  const appearance = useMemo(() => buildStripeAppearance(resolvedTheme), [resolvedTheme]);
  const addressOptions = useMemo(
    () => ({ mode: 'setup' as const, currency: 'usd', paymentMethodTypes: ['card'], appearance }),
    [appearance],
  );
  const cardOptions = useMemo(
    () => (clientSecret ? { clientSecret, appearance } : null),
    [clientSecret, appearance],
  );

  const summaryFor = (phase: Phase) => (typeof summary === 'function' ? summary(phase) : summary);

  if (singleStep) {
    return (
      <Elements key="single" stripe={stripePromise} options={addressOptions}>
        <SingleStep
          requestSetupIntent={requestSetupIntent}
          onConfirmed={onConfirmed}
          onCancel={onCancel}
          summary={summaryFor('payment')}
          testIds={testIds}
          submitLabel={submitLabel}
          heading={addressHeading}
          cardNote={cardNote}
          disableAddressAutocomplete={disableAddressAutocomplete}
        />
      </Elements>
    );
  }

  if (!cardOptions) {
    return (
      <Elements key="address" stripe={stripePromise} options={addressOptions}>
        <AddressStep
          onAddressConfirmed={async (address) => {
            const result = await requestSetupIntent(address);
            setClientSecret(result.clientSecret);
          }}
          onCancel={onCancel}
          summary={summaryFor('address')}
          testIds={testIds}
          submitLabel="Continue"
          heading={addressHeading}
          disableAddressAutocomplete={disableAddressAutocomplete}
        />
      </Elements>
    );
  }

  return (
    <Elements key="card" stripe={stripePromise} options={cardOptions}>
      <CardStep
        onCancel={onCancel}
        onConfirmed={onConfirmed}
        summary={summaryFor('payment')}
        testIds={testIds}
        submitLabel={submitLabel}
        cardNote={cardNote}
      />
    </Elements>
  );
}
